import produce, { enableES5 } from 'immer';
import { Action } from 'redux';
import { RootState } from '.';

enableES5();

// =============================================================================
// State
// =============================================================================
interface AppState {
  isLoggedIn: boolean;
  inactivityMinutes: number;
  emptyState: boolean;
  isIdleTimeoutEnabled: boolean;
  csrfToken: string;
  username: string;
  isSessionInactive: boolean;
}

export const INITIAL_APP_STATE: AppState = {
  isLoggedIn: false,
  inactivityMinutes: 0,
  emptyState: false,
  isIdleTimeoutEnabled: true,
  csrfToken: '',
  username: '',
  isSessionInactive: false,
};

// =============================================================================
// Action Type
// =============================================================================
enum AppActionTypes {
  USER_LOGGED_IN_SUCCESS = 'app/user_logged_in_success',
  USER_LOGGED_OUT_SUCCESS = 'app/user_logged_out_success',
  UPDATE_INACTIVITY_MINUTES = 'app/update_inactivity_minutes',
  UPDATE_EMPTY_STATE = 'app/update_empty_state',
  UPDATE_SERVER_DATE_TIME = 'app/update_server_date_time',
  ENABLE_IDLE_TIMEOUT = 'app/enable_idle_timeout',
  DISABLE_IDLE_TIMEOUT = 'app/disable_idle_timeout',
  UPDATE_CSRF_TOKEN = 'app/update_csrf_token',
  UPDATE_USERNAME = 'app/update_username',
  USER_SESSION_INACTIVE_TRUE = 'app/user_session_inactive_true',
}

// =============================================================================
// Actions
// =============================================================================
interface UserLoggedInSuccessAction extends Action<AppActionTypes.USER_LOGGED_IN_SUCCESS> {}

interface UserLoggedOutSuccessAction extends Action<AppActionTypes.USER_LOGGED_OUT_SUCCESS> {}

interface UpdateInactivityMinutesAction extends Action<AppActionTypes.UPDATE_INACTIVITY_MINUTES> {
  payload: number;
}

interface UpdateEmptyStateAction extends Action<AppActionTypes.UPDATE_EMPTY_STATE> {
  payload: boolean;
}

interface UpdateServerDateTime extends Action<AppActionTypes.UPDATE_SERVER_DATE_TIME> {
  payload: string;
}

interface EnableIdleTimeoutAction extends Action<AppActionTypes.ENABLE_IDLE_TIMEOUT> {}

interface DisableIdleTimeoutAction extends Action<AppActionTypes.DISABLE_IDLE_TIMEOUT> {}

interface UpdateCsrfToken extends Action<AppActionTypes.UPDATE_CSRF_TOKEN> {
  payload: string;
}

interface UpdateUsername extends Action<AppActionTypes.UPDATE_USERNAME> {
  payload: string;
}

interface SetUserSessionInactiveTrueAction
    extends Action<AppActionTypes.USER_SESSION_INACTIVE_TRUE> {
}

type AppActions =
    | UserLoggedInSuccessAction
    | UserLoggedOutSuccessAction
    | UpdateInactivityMinutesAction
    | UpdateEmptyStateAction
    | UpdateServerDateTime
    | EnableIdleTimeoutAction
    | DisableIdleTimeoutAction
    | UpdateCsrfToken
    | UpdateUsername
    | SetUserSessionInactiveTrueAction;

// =============================================================================
// Action Creators
// =============================================================================
export const userLoggedInSuccess = (): UserLoggedInSuccessAction => ({
  type: AppActionTypes.USER_LOGGED_IN_SUCCESS,
});

export const userLoggedOutSuccess = (): UserLoggedOutSuccessAction => ({
  type: AppActionTypes.USER_LOGGED_OUT_SUCCESS,
});

export const updateInactivityMinutes = (
  inactivityMinutes: number
): UpdateInactivityMinutesAction => ({
  type: AppActionTypes.UPDATE_INACTIVITY_MINUTES,
  payload: inactivityMinutes,
});

export const updateEmptyState = (emptyState: boolean): UpdateEmptyStateAction => ({
  type: AppActionTypes.UPDATE_EMPTY_STATE,
  payload: emptyState,
});

export const updateServerDateTime = (serverDateTime: string): UpdateServerDateTime => ({
  type: AppActionTypes.UPDATE_SERVER_DATE_TIME,
  payload: serverDateTime,
});

export const enableIdleTimeout = (): EnableIdleTimeoutAction => ({
  type: AppActionTypes.ENABLE_IDLE_TIMEOUT,
});

export const disableIdleTimeout = (): DisableIdleTimeoutAction => ({
  type: AppActionTypes.DISABLE_IDLE_TIMEOUT,
});
export const updateCsrfToken = (csrfToken: string): UpdateCsrfToken => ({
  type: AppActionTypes.UPDATE_CSRF_TOKEN,
  payload: csrfToken,
});

export const updateUsername = (username: string): UpdateUsername => ({
  type: AppActionTypes.UPDATE_USERNAME,
  payload: username,
});

export const setUserSessionInactiveTrue = (): SetUserSessionInactiveTrueAction => ({
  type: AppActionTypes.USER_SESSION_INACTIVE_TRUE,
});

// =============================================================================
// Reducer
// =============================================================================
export const appReducer = produce((draft: AppState, action: AppActions) => {
  switch (action.type) {
    case AppActionTypes.USER_LOGGED_IN_SUCCESS: {
      draft.isLoggedIn = true;
      draft.isSessionInactive = false;
      break;
    }
    case AppActionTypes.USER_LOGGED_OUT_SUCCESS:
      draft.isLoggedIn = false;
      draft.inactivityMinutes = 0;
      break;
    case AppActionTypes.UPDATE_INACTIVITY_MINUTES:
      draft.inactivityMinutes = action.payload;
      break;
    case AppActionTypes.UPDATE_EMPTY_STATE:
      draft.emptyState = action.payload;
      break;
    case AppActionTypes.ENABLE_IDLE_TIMEOUT:
      draft.isIdleTimeoutEnabled = true;
      break;
    case AppActionTypes.DISABLE_IDLE_TIMEOUT:
      draft.isIdleTimeoutEnabled = false;
      break;
    case AppActionTypes.UPDATE_CSRF_TOKEN:
      draft.csrfToken = action.payload;
      break;
    case AppActionTypes.UPDATE_USERNAME:
      draft.username = action.payload;
      break;
    case AppActionTypes.USER_SESSION_INACTIVE_TRUE:
      draft.isSessionInactive = true;
      break;
    default:
      break;
  }
}, INITIAL_APP_STATE);

// =============================================================================
// Selectors
// =============================================================================
export const selectIsUserLoggedIn = (state: RootState) => state.app.isLoggedIn;
export const selectInactivityMinutes = (state: RootState) => state.app.inactivityMinutes;
export const selectEmptyState = (state: RootState) => state.app.emptyState;
export const selectIsIdleTimeoutEnabled = (state: RootState) => state.app.isIdleTimeoutEnabled;
export const selectCsrfToken = (state: RootState) => state.app.csrfToken;
export const selectUsername = (state: RootState) => state.app.username;
export const selectIsUserSessionInactive = (state: RootState) => state.app.isSessionInactive;
